import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { AUTH } from '@/constants/testIds';
import { ArrowRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/dashboard';
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState(params.get('reason') === 'cap' ? '' : 'demo@revcore.ai');
  const [password, setPassword] = useState(params.get('reason') === 'cap' ? '' : 'Demo123!Demo');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        await login(email, password);
        toast.success('Welcome back');
      } else {
        await register(email, password, name || email.split('@')[0]);
        toast.success('Account created');
      }
      navigate(next, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 grid lg:grid-cols-2 gap-10 items-stretch">
      {/* Brand panel */}
      <div className="hidden lg:flex relative card-base p-10 overflow-hidden flex-col justify-between min-h-[560px]">
        <div className="absolute -right-12 -top-16 h-72 w-72 rounded-full bg-[#FF6B1A]/15 blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-16 h-56 w-56 rounded-full bg-[#39FF14]/10 blur-3xl pointer-events-none" />
        <div className="relative">
          <span className="text-[10px] uppercase tracking-widest text-[#39FF14]">// the operator's stack</span>
          <h2 className="display text-3xl md:text-4xl font-semibold mt-2 leading-tight">
            Step inside <span className="gradient-text">RevCore</span>.
          </h2>
          <p className="text-white/65 mt-3 max-w-md">
            Sign in to save your agents, conversations, and ship faster than your competitors.
          </p>
        </div>
        <div className="relative grid grid-cols-3 gap-3">
          {['Hire', 'Tune', 'Deploy'].map((s, i) => (
            <div key={s} className="glass p-3 rounded-xl text-center">
              <div className="mono text-[10px] text-white/40">step 0{i + 1}</div>
              <div className="display text-base mt-1">{s}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="card-base p-8 lg:p-10">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-xl grid place-items-center bg-gradient-to-br from-[#FF6B1A] to-[#FF8A4A]">
            <Sparkles className="h-5 w-5 text-white" />
          </div>
          <div className="display text-lg font-semibold">{mode === 'login' ? 'Welcome back' : 'Create your account'}</div>
        </div>
        <p className="text-sm text-white/60 mb-6">
          {mode === 'login' ? 'Sign in to keep your agents and conversations.' : 'Free forever for unlimited tries.'}
        </p>

        <button
          onClick={googleLogin}
          data-testid={AUTH.google}
          className="btn-secondary w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium"
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09A6.6 6.6 0 0 1 5.48 12c0-.72.13-1.43.36-2.09V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
          Continue with Google
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-border/60" />
          <span className="text-[10px] uppercase tracking-widest text-white/40">or with email</span>
          <div className="h-px flex-1 bg-border/60" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {mode === 'register' && (
            <Field label="Name">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="input" />
            </Field>
          )}
          <Field label="Email">
            <input data-testid={AUTH.email} value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="input" />
          </Field>
          <Field label="Password">
            <input data-testid={AUTH.password} value={password} onChange={(e) => setPassword(e.target.value)} type="password" required minLength={6} className="input" />
          </Field>
          <button
            type="submit"
            data-testid={AUTH.submit}
            disabled={loading}
            className="btn-primary w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-medium disabled:opacity-50"
          >
            {loading ? 'Working…' : (mode === 'login' ? 'Sign in' : 'Create account')}
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        <button
          data-testid={AUTH.switchMode}
          type="button"
          onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
          className="mt-6 text-xs text-white/60 hover:text-white"
        >
          {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>

        <div className="mt-6 text-[11px] text-white/40">
          Demo: <span className="mono">demo@revcore.ai / Demo123!Demo</span>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-white/55 mb-1.5">{label}</div>
      {children}
    </label>
  );
}
